import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import {
  getAddAllRefusal,
  isAddAllOrNothingOn,
  isDisableUnfulfillableAddAllOn,
} from '../../utils/promotions/addAllAvailability'
import { generateDataDogContext } from '../../utils/dataDog'
import { getPromotionItemsToAdd } from '../../utils/promotions/promotionItemsToAdd'
import useAppSelector from '../useAppSelector'
import { useAddPrefillCart } from '../prefillCart/useAddPrefillCart'
import { IPromotionShortcutData } from '../../interfaces'

export { EmptyPackage } from '../../utils/promotions/promotionItemsToAdd'

/**
 * Custom React hook to provide logic for adding products from promotions.
 *
 * This hook is used to generate and dispatch a cart update based on a promotion (discount, bundle, etc.)
 * when a user selects a promotional action from product detail promotion button.
 *
 * It prepares the prefill cart payload according to the promotion's rules, current selections,
 * and autoAdd configuration. The payload is sent through `addPrefillCart`, so that the cart displays the correct
 * promoted items for checkout.
 *
 * Under `globals.promotions.disableUnfulfillableAddAll` it sends nothing for a
 * promotion or group the stock lookup found unfulfillable, nor for a promotion
 * with several buys and several gets groups, whose button is hidden, and
 * reports each refused click to Datadog. With the flag off nothing here
 * changes.
 *
 * On a `delivery-ui-components` that does not read `buttonDisabled` the
 * button stays drawn and pressable, and this refusal is the only thing
 * between the shopper and the request — the click silently does nothing but
 * report. Do not enable the flag before the library reads the props.
 *
 * Under `globals.promotions.addAllOrNothing` it asks storefront for all or
 * nothing, so a combo with a line that can't be added is rejected whole
 * instead of filled partially. This button is the only caller that does.
 *
 * @returns {{
 *   addProductFromPromotion: ({ promotion, groupIdentifier }: IPromotionShortcutData) => void
 * }} An object containing the `addProductFromPromotion` handler function.
 *
 */
export const useAddProductFromPromotion = () => {
  const selectedProduct = useAppSelector(
    (state) => state.catalogSlice.selectedProduct,
  )
  const autoAdd = useAppSelector(
    (state) =>
      state.defaultSlice.sessionData?.configuration?.promotions?.autoAdd,
  )
  const storefrontName =
    useAppSelector((state) => state.defaultSlice.storeName) || ''
  const sessionUid =
    useAppSelector((state) => state.defaultSlice.sessionId) || ''

  const disableUnfulfillableAddAll = useAppSelector((state) =>
    isDisableUnfulfillableAddAllOn(state.defaultSlice.config),
  )
  const addAllOrNothing = useAppSelector((state) =>
    isAddAllOrNothingOn(state.defaultSlice.config),
  )
  const promotionsAvailability = useAppSelector(
    (state) => state.catalogSlice.promotionsAvailability,
  )

  const { addPrefillCart } = useAddPrefillCart()

  /**
   * Handler to add products to cart from a promotion.
   *
   * @param {IPromotionShortcutData} params - The data for the promotion shortcut.
   * @param {any} params.promotion - The promotion object selected by the user.
   * @param {string} [params.groupIdentifier] - Optional identifier for a group of promotion items.
   */
  const addProductFromPromotion = ({
    promotion,
    groupIdentifier,
  }: IPromotionShortcutData) => {
    if (promotion) {
      const refusal = disableUnfulfillableAddAll
        ? getAddAllRefusal({
            availability: promotionsAvailability,
            sku: selectedProduct?.sku,
            promotion,
            groupIdentifier,
          })
        : null
      if (refusal) {
        // No `startView`: this runs on the product page, whose manually
        // tracked view a restart would end.
        datadogRum.addError(
          new Error('promotion add-all click refused'),
          generateDataDogContext({
            title: 'promotion add-all click refused',
            extraInfo: {
              function: 'useAddProductFromPromotion',
              promotionId: promotion.id,
              groupIdentifier: groupIdentifier ?? null,
              reason: refusal,
            },
          }),
        )
        return
      }

      const items = getPromotionItemsToAdd({
        promotion,
        groupIdentifier,
        autoAdd: !!autoAdd,
        selectedProduct,
      })

      addPrefillCart({
        items,
        storefrontName,
        sessionUid,
        referrer: Referrers.PROMOTION_DETAIL,
        replaceCart: false,
        // Spread only when on, so the switch off sends today's exact call.
        ...(addAllOrNothing ? { allOrNothing: true } : {}),
      })
    }
  }
  return { addProductFromPromotion }
}

export default useAddProductFromPromotion
